import { adressePhotoProfil } from '@/shared/comptes/photo';
import { z } from 'zod';
import type { Utilisateur } from '@/shared/auth/types';
import { comparerCodes, departementDuCodePostal } from '@/shared/referentiel/departements';
import { estMetier } from '@/shared/referentiel/etapes';
import type {
  Contact,
  DocumentConformite,
  DocumentEntreprise,
  Entreprise,
  FicheEntreprise,
  FichePartenaire,
  Partenaire,
  Position,
  PrecisionPosition,
  ResumeEntreprise,
  ResumePartenaire,
  SaisieContact,
  SaisieContactPartenaire,
  ContactPartenaire,
  SaisieEntreprise,
  SaisiePartenaire,
  SaisieSite,
  Site,
} from '../types';

/*
 * Les lignes telles que les renvoie PostgREST, vérifiées avant usage : une
 * colonne renommée côté base doit casser ici, lisiblement, pas dans un écran.
 * Tout ce fichier est pur — ni réseau ni client Supabase — pour être testé seul.
 */

const trierParNom = <T extends { nom: string }>(liste: T[]): T[] =>
  [...liste].sort((a, b) => a.nom.localeCompare(b.nom, 'fr'));

const plusRecentDabord = <T extends { ajouteLe: string }>(liste: T[]): T[] =>
  [...liste].sort((a, b) => b.ajouteLe.localeCompare(a.ajouteLe));

// ── Fichiers ─────────────────────────────────────────────────────────────────

export const COLONNES_FICHIER = 'id, nom, taille, type_mime, chemin, ajoute_le, ajoute_par';

export const champsFichier = {
  id: z.string(),
  nom: z.string(),
  /* `bigint` : PostgREST le rend en nombre JSON ; on accepte aussi la chaîne. */
  taille: z.coerce.number(),
  type_mime: z.string(),
  chemin: z.string(),
  ajoute_le: z.string(),
  ajoute_par: z.string().nullable(),
};

export function versFichier(l: {
  id: string;
  nom: string;
  taille: number;
  type_mime: string;
  chemin: string;
  ajoute_le: string;
  ajoute_par: string | null;
}) {
  return {
    id: l.id,
    nom: l.nom,
    taille: l.taille,
    typeMime: l.type_mime,
    chemin: l.chemin,
    ajouteLe: l.ajoute_le,
    ajoutePar: l.ajoute_par,
  };
}

// ── Entreprises ──────────────────────────────────────────────────────────────

export const COLONNES_ENTREPRISE =
  'id, raison_sociale, forme, siret, activite, adresse, code_postal, commune, notes, cree_le, non_realisable_le, non_realisable_motif, non_realisable_par';
export const COLONNES_POSITION = 'latitude, longitude, precision_position';
export const COLONNES_SITE = `id, entreprise_id, nom, adresse, code_postal, commune, departement, notes, ${COLONNES_POSITION}`;
export const COLONNES_CONTACT =
  'id, entreprise_id, prenom, nom, fonction, telephone, email, sur_place';
const COLONNES_DOCUMENT_ENTREPRISE = `${COLONNES_FICHIER}, categorie`;

/** Une requête pour la liste : les compteurs viennent des tables liées, pas d'un appel par ligne. */
export const SELECTION_RESUME_ENTREPRISE = `${COLONNES_ENTREPRISE}, sites(departement), contacts(id), documents_entreprise(categorie)`;

export const SELECTION_FICHE_ENTREPRISE = `${COLONNES_ENTREPRISE}, sites(${COLONNES_SITE}), contacts(${COLONNES_CONTACT}), documents_entreprise(${COLONNES_DOCUMENT_ENTREPRISE})`;

export const ligneEntreprise = z.object({
  id: z.string(),
  raison_sociale: z.string(),
  forme: z.string(),
  siret: z.string(),
  activite: z.string(),
  adresse: z.string(),
  code_postal: z.string(),
  commune: z.string(),
  notes: z.string(),
  cree_le: z.string(),
  /* db/046 : absents d'une ligne plus ancienne, la croix n'y est pas. */
  non_realisable_le: z.string().nullable().default(null),
  non_realisable_motif: z.string().default(''),
  non_realisable_par: z.string().default(''),
});

const categorieDocument = z.enum(['devis-signe', 'administratif', 'autre']);

/* `double precision` : un nombre JSON chez PostgREST (db/004-positions.sql). */
export const champsPosition = {
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  precision_position: z.enum(['adresse', 'commune', 'manuelle']).nullable(),
};

export const ligneSite = z.object({
  id: z.string(),
  entreprise_id: z.string(),
  nom: z.string(),
  adresse: z.string(),
  code_postal: z.string(),
  commune: z.string(),
  departement: z.string(),
  notes: z.string(),
  ...champsPosition,
});

export const ligneContact = z.object({
  id: z.string(),
  entreprise_id: z.string(),
  prenom: z.string().default(''),
  nom: z.string(),
  fonction: z.string(),
  telephone: z.string(),
  email: z.string(),
  sur_place: z.boolean(),
});

const ligneDocumentEntreprise = z.object({ ...champsFichier, categorie: categorieDocument });

export const ligneResumeEntreprise = ligneEntreprise.extend({
  sites: z.array(z.object({ departement: z.string() })),
  contacts: z.array(z.object({ id: z.string() })),
  documents_entreprise: z.array(z.object({ categorie: categorieDocument })),
});

export const ligneFicheEntreprise = ligneEntreprise.extend({
  sites: z.array(ligneSite),
  contacts: z.array(ligneContact),
  documents_entreprise: z.array(ligneDocumentEntreprise),
});

export function versEntreprise(l: z.infer<typeof ligneEntreprise>): Entreprise {
  return {
    id: l.id,
    raisonSociale: l.raison_sociale,
    forme: l.forme,
    siret: l.siret,
    activite: l.activite,
    adresse: l.adresse,
    codePostal: l.code_postal,
    commune: l.commune,
    notes: l.notes,
    creeLe: l.cree_le,
    nonRealisable: l.non_realisable_le
      ? { le: l.non_realisable_le, motif: l.non_realisable_motif, par: l.non_realisable_par }
      : null,
  };
}

export function versResumeEntreprise(l: z.infer<typeof ligneResumeEntreprise>): ResumeEntreprise {
  const departements = [...new Set(l.sites.map((s) => s.departement).filter((d) => d !== ''))];
  return {
    ...versEntreprise(l),
    nbSites: l.sites.length,
    nbContacts: l.contacts.length,
    nbDocuments: l.documents_entreprise.length,
    devisSigne: l.documents_entreprise.some((d) => d.categorie === 'devis-signe'),
    departements: departements.sort(comparerCodes),
  };
}

export function versSite(l: z.infer<typeof ligneSite>): Site {
  return {
    id: l.id,
    entrepriseId: l.entreprise_id,
    nom: l.nom,
    adresse: l.adresse,
    codePostal: l.code_postal,
    commune: l.commune,
    departement: l.departement,
    notes: l.notes,
    position: versPosition(l),
  };
}

export function versContact(l: z.infer<typeof ligneContact>): Contact {
  return {
    id: l.id,
    entrepriseId: l.entreprise_id,
    prenom: l.prenom,
    nom: l.nom,
    fonction: l.fonction,
    telephone: l.telephone,
    email: l.email,
    surPlace: l.sur_place,
  };
}

function versDocumentEntreprise(l: z.infer<typeof ligneDocumentEntreprise>): DocumentEntreprise {
  return { ...versFichier(l), categorie: l.categorie };
}

export function versFicheEntreprise(l: z.infer<typeof ligneFicheEntreprise>): FicheEntreprise {
  return {
    ...versEntreprise(l),
    sites: trierParNom(l.sites.map(versSite)),
    contacts: trierParNom(l.contacts.map(versContact)),
    documents: plusRecentDabord(l.documents_entreprise.map(versDocumentEntreprise)),
  };
}

export function ligneDeSaisieEntreprise(s: SaisieEntreprise) {
  return {
    raison_sociale: s.raisonSociale.trim(),
    forme: s.forme.trim(),
    siret: s.siret.trim(),
    activite: s.activite.trim(),
    adresse: s.adresse.trim(),
    code_postal: s.codePostal.trim(),
    commune: s.commune.trim(),
    notes: s.notes,
  };
}

/** Le département se déduit du code postal ; vide s'il n'est pas reconnu. */
export function ligneDeSaisieSite(s: SaisieSite) {
  const codePostal = s.codePostal.trim();
  return {
    nom: s.nom.trim(),
    adresse: s.adresse.trim(),
    code_postal: codePostal,
    commune: s.commune.trim(),
    departement: departementDuCodePostal(codePostal)?.code ?? '',
    notes: s.notes,
  };
}

// ── Positions (S5) ───────────────────────────────────────────────────────────

export interface ColonnesPosition {
  latitude: number | null;
  longitude: number | null;
  precision_position: PrecisionPosition | null;
}

/** Les trois colonnes vont ensemble (db/004-positions.sql) : qu'une manque, pas de position. */
export function versPosition(l: ColonnesPosition): Position | null {
  if (l.latitude === null || l.longitude === null || l.precision_position === null) return null;
  return { latitude: l.latitude, longitude: l.longitude, precision: l.precision_position };
}

export function ligneDePosition(p: Position | null): ColonnesPosition {
  return {
    latitude: p?.latitude ?? null,
    longitude: p?.longitude ?? null,
    precision_position: p?.precision ?? null,
  };
}

/** Les bornes que la base impose aussi (`sites_coordonnees_check`). */
export function coordonneesValides(c: { latitude: number; longitude: number }): boolean {
  return (
    Number.isFinite(c.latitude) &&
    Number.isFinite(c.longitude) &&
    c.latitude >= -90 &&
    c.latitude <= 90 &&
    c.longitude >= -180 &&
    c.longitude <= 180
  );
}

/** Ce que le géocodeur lit d'un site, comparé tel que la base le stocke. */
function lieuNormalise(l: { adresse: string; codePostal: string; commune: string }) {
  return {
    adresse: l.adresse.trim(),
    codePostal: l.codePostal.trim(),
    commune: l.commune.trim(),
  };
}

/**
 * Faut-il relancer le géocodage en enregistrant un site modifié ?
 *
 * - l'adresse, le code postal ou la commune ont changé : oui, toujours — même
 *   une position placée à la main ne vaut plus pour une autre adresse ;
 * - rien de cela n'a changé : on garde la position, manuelle ou non ; on ne
 *   retente que si le site n'en a pas (géocodage raté la dernière fois).
 */
export function doitRegeocoder(
  avant: { adresse: string; codePostal: string; commune: string; position: Position | null },
  apres: { adresse: string; codePostal: string; commune: string }
): boolean {
  const a = lieuNormalise(avant);
  const b = lieuNormalise(apres);
  const adresseChangee =
    a.adresse !== b.adresse || a.codePostal !== b.codePostal || a.commune !== b.commune;
  return adresseChangee || avant.position === null;
}

export function ligneDeSaisieContact(s: SaisieContact) {
  return {
    prenom: s.prenom.trim(),
    nom: s.nom.trim(),
    fonction: s.fonction.trim(),
    telephone: s.telephone.trim(),
    email: s.email.trim(),
    sur_place: s.surPlace,
  };
}

// ── Partenaires ──────────────────────────────────────────────────────────────

export const COLONNES_PARTENAIRE =
  'id, raison_sociale, type, siret, contact_prenom, contact_nom, telephone, email, adresse, code_postal, commune, etapes, zones, notes, cree_le, logo';
const COLONNES_CONFORMITE = `${COLONNES_FICHIER}, partenaire_id, type, expire_le`;
const COLONNES_COMPTE =
  'id, email, nom, prenom, nom_famille, photo, role, niveau, partenaire_id, actif';

export const SELECTION_RESUME_PARTENAIRE = `${COLONNES_PARTENAIRE}, documents_conformite(${COLONNES_CONFORMITE}), profils(id)`;
const COLONNES_CONTACT_PARTENAIRE = 'id, partenaire_id, prenom, nom, fonction, telephone, email';
export const SELECTION_FICHE_PARTENAIRE = `${COLONNES_PARTENAIRE}, documents_conformite(${COLONNES_CONFORMITE}), profils(${COLONNES_COMPTE}), contacts_partenaires(${COLONNES_CONTACT_PARTENAIRE})`;

export const ligneContactPartenaire = z.object({
  id: z.string(),
  partenaire_id: z.string(),
  prenom: z.string(),
  nom: z.string(),
  fonction: z.string(),
  telephone: z.string(),
  email: z.string(),
});

export function versContactPartenaire(
  l: z.infer<typeof ligneContactPartenaire>
): ContactPartenaire {
  return {
    id: l.id,
    partenaireId: l.partenaire_id,
    prenom: l.prenom,
    nom: l.nom,
    fonction: l.fonction,
    telephone: l.telephone,
    email: l.email,
  };
}

export function ligneDeSaisieContactPartenaire(s: SaisieContactPartenaire) {
  return {
    prenom: s.prenom.trim(),
    nom: s.nom.trim(),
    fonction: s.fonction.trim(),
    telephone: s.telephone.trim(),
    email: s.email.trim(),
  };
}
export const SELECTION_COMPTE = `${COLONNES_COMPTE}, partenaires(raison_sociale, logo)`;

export const lignePartenaire = z.object({
  id: z.string(),
  raison_sociale: z.string(),
  type: z.enum(['sous-traitant', 'equipe-interne']),
  siret: z.string(),
  contact_prenom: z.string().default(''),
  contact_nom: z.string(),
  telephone: z.string(),
  email: z.string(),
  adresse: z.string(),
  code_postal: z.string(),
  commune: z.string(),
  /* La base refuse déjà une étape inconnue ; on filtre quand même, sans casser. */
  etapes: z.array(z.string()).transform((etapes) => etapes.filter(estMetier)),
  zones: z.array(z.string()),
  notes: z.string(),
  cree_le: z.string(),
  logo: z.string().nullable().default(null),
});

const ligneConformite = z.object({
  ...champsFichier,
  partenaire_id: z.string(),
  type: z.enum(['kbis', 'decennale', 'urssaf', 'qualification', 'autre']),
  expire_le: z.string().nullable(),
});

const roleCompte = z.enum(['bureau', 'partenaire']);

const ligneCompteDuPartenaire = z.object({
  id: z.string(),
  email: z.string(),
  nom: z.string(),
  prenom: z.string().default(''),
  nom_famille: z.string().default(''),
  photo: z.string().nullable().default(null),
  role: roleCompte,
  niveau: z.enum(['ceo', 'administrateur', 'assistant']).nullable().default(null),
  partenaire_id: z.string().nullable(),
  actif: z.boolean(),
});

export const ligneCompte = ligneCompteDuPartenaire.extend({
  partenaires: z
    .object({ raison_sociale: z.string(), logo: z.string().nullable().default(null) })
    .nullable(),
});

export const ligneResumePartenaire = lignePartenaire.extend({
  documents_conformite: z.array(ligneConformite),
  profils: z.array(z.object({ id: z.string() })),
});

export const ligneFichePartenaire = lignePartenaire.extend({
  documents_conformite: z.array(ligneConformite),
  profils: z.array(ligneCompteDuPartenaire),
  contacts_partenaires: z.array(ligneContactPartenaire).default([]),
});

export function versPartenaire(l: z.infer<typeof lignePartenaire>): Partenaire {
  return {
    id: l.id,
    raisonSociale: l.raison_sociale,
    type: l.type,
    siret: l.siret,
    contactPrenom: l.contact_prenom,
    contactNom: l.contact_nom,
    telephone: l.telephone,
    email: l.email,
    adresse: l.adresse,
    codePostal: l.code_postal,
    commune: l.commune,
    etapes: l.etapes,
    zones: [...l.zones].sort(comparerCodes),
    notes: l.notes,
    creeLe: l.cree_le,
    logo: adressePhotoProfil(l.logo),
  };
}

function versConformite(l: z.infer<typeof ligneConformite>): DocumentConformite {
  return { ...versFichier(l), type: l.type, expireLe: l.expire_le };
}

export function versResumePartenaire(l: z.infer<typeof ligneResumePartenaire>): ResumePartenaire {
  return {
    ...versPartenaire(l),
    conformite: plusRecentDabord(l.documents_conformite.map(versConformite)),
    nbComptes: l.profils.length,
  };
}

export function versFichePartenaire(l: z.infer<typeof ligneFichePartenaire>): FichePartenaire {
  return {
    ...versPartenaire(l),
    conformite: plusRecentDabord(l.documents_conformite.map(versConformite)),
    comptes: l.profils
      .map((p) =>
        versUtilisateur({ ...p, partenaires: { raison_sociale: l.raison_sociale, logo: l.logo } })
      )
      .sort((a, b) => a.email.localeCompare(b.email, 'fr')),
    contacts: l.contacts_partenaires
      .map(versContactPartenaire)
      .sort((a, b) => `${a.nom} ${a.prenom}`.localeCompare(`${b.nom} ${b.prenom}`, 'fr')),
  };
}

export function versUtilisateur(l: z.infer<typeof ligneCompte>): Utilisateur {
  return {
    id: l.id,
    email: l.email,
    nom: l.nom,
    prenom: l.prenom,
    nomFamille: l.nom_famille,
    photo: adressePhotoProfil(l.photo),
    role: l.role,
    niveau: l.niveau,
    partenaireId: l.partenaire_id,
    partenaireNom: l.partenaires?.raison_sociale ?? null,
    partenaireLogo: adressePhotoProfil(l.partenaires?.logo),
    actif: l.actif,
  };
}

export function ligneDeSaisiePartenaire(s: SaisiePartenaire) {
  return {
    raison_sociale: s.raisonSociale.trim(),
    type: s.type,
    siret: s.siret.trim(),
    contact_prenom: s.contactPrenom.trim(),
    contact_nom: s.contactNom.trim(),
    telephone: s.telephone.trim(),
    email: s.email.trim(),
    adresse: s.adresse.trim(),
    code_postal: s.codePostal.trim(),
    commune: s.commune.trim(),
    etapes: [...new Set(s.etapes)],
    zones: [...new Set(s.zones)],
    notes: s.notes,
  };
}

// ── Erreurs ──────────────────────────────────────────────────────────────────

/* Codes Postgres (et PostgREST) qui disent quelque chose d'utile à l'écran. Les
   autres retombent sur le message propre à l'action : jamais le texte brut de
   la base, qui parle anglais et de colonnes. */
const PAR_CODE: Record<string, string> = {
  '42501': 'Vous n’avez pas le droit de faire cette action.',
  '23514': 'Une valeur saisie n’est pas acceptée.',
  '23505': 'Cet élément existe déjà.',
  PGRST301: 'Votre session a expiré : reconnectez-vous.',
};

/**
 * Le message à montrer pour une erreur Supabase. `propres` passe avant les
 * messages génériques : « ce partenaire a encore des comptes » dit plus qu'une
 * violation de clé étrangère.
 */
export function messageErreur(
  erreur: { code?: string | undefined } | null | undefined,
  defaut: string,
  propres: Readonly<Record<string, string>> = {}
): string {
  const code = erreur?.code;
  if (code === undefined) return defaut;
  return propres[code] ?? PAR_CODE[code] ?? defaut;
}

/** Le `{ message }` d'une réponse de /api, s'il y en a un lisible. */
export function messageDeReponse(corps: unknown): string | null {
  if (typeof corps !== 'object' || corps === null || !('message' in corps)) return null;
  const { message } = corps;
  return typeof message === 'string' && message.trim() !== '' ? message : null;
}
